import React, { useRef, useEffect, useState, useMemo } from 'react';

import { PlusOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Tag, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styled, { keyframes, css } from 'styled-components';

import BatchScanModal from './BatchScanModal';

interface Product {
  id?: string;
  name?: string;
  sku?: string;
  barcode?: string;
  required_serial_numbers_count?: number;
  [key: string]: unknown;
}

interface InventoryType {
  id?: string;
  name?: string;
  [key: string]: unknown;
}

export interface OrderItem {
  id: string;
  quantity: number;
  shipped_quantity?: number;
  batch?: string | null;
  expiration_date?: string | null;
  inventory_type?: InventoryType;
  product?: Product;
  note?: string | null;
  [key: string]: unknown;
}

interface ParcelItem {
  fulfillment_item_id?: string | null;
  product_id?: string;
  quantity?: number;
  batch?: string | null;
  serial_numbers?: string[];
  required_serial_numbers_count?: number;
  [key: string]: unknown;
}

export interface Parcel {
  parcel_items?: ParcelItem[];
  [key: string]: unknown;
}

export interface OrderItemsTableProps {
  orderItems?: OrderItem[];
  parcels?: Parcel[];
  highlightedItemId?: string | null;
  onBatchScan?: (item: OrderItem, quantity: number, serialNumbers?: string[]) => void;
  onModalOpenChange?: (open: boolean) => void;
  onSerialNumberClick?: (item: OrderItem, serial: string, parcelIndex: number) => void;
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ItemsWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px;
`;

const highlightPulse = keyframes`
  0% {
    background: rgba(34, 197, 94, 0.2);
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
  }
  50% {
    background: rgba(34, 197, 94, 0.1);
    box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.05);
  }
  100% {
    background: transparent;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
`;

const ProductCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(14, 116, 144, 0.3);
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  }
`;

const ProductHeader = styled.div`
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const ProductMainInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProductName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.3;
`;

const ProductMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #64748b;
`;

const SkuLabel = styled.span`
  font-family: monospace;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
`;

const TotalQuantityInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const TotalQuantityValue = styled.div<{ completed?: boolean }>`
  font-size: 24px;
  font-weight: 800;
  color: ${props => props.completed ? '#22c55e' : '#0e7490'};
  font-family: monospace;
  line-height: 1;
`;

const TotalQuantityLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LinesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LineItem = styled.div<{ $highlighted?: boolean; $recentlyScanned?: boolean }>`
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.04);
  transition: background-color 0.2s ease;
  scroll-margin: 100px;

  &:last-child {
    border-bottom: none;
  }

  ${props => props.$highlighted && css`
    animation: ${highlightPulse} 2s ease-in-out infinite;
    background: rgba(34, 197, 94, 0.05);
  `}

  ${props => props.$recentlyScanned && !props.$highlighted && css`
    background: rgba(34, 197, 94, 0.03);
  `}
`;

const LineDetails = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
`;

const DetailBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DetailLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
`;

const DetailValue = styled.div<{ mono?: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${props => props.mono && css`
    font-family: monospace;
  `}
`;

const LineQuantity = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
  justify-content: flex-end;
`;

const QuantityBox = styled.div<{ completed?: boolean }>`
  background: ${props => props.completed ? 'rgba(34, 197, 94, 0.1)' : 'rgba(226, 232, 240, 0.6)'};
  padding: 4px 10px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const QtyVal = styled.span<{ completed?: boolean }>`
  font-weight: 700;
  font-family: monospace;
  color: ${props => props.completed ? '#16a34a' : '#475569'};
`;

const QtyTotal = styled.span`
  color: #94a3b8;
  font-size: 12px;
`;

const SerialSection = styled.div`
  padding: 8px 20px 12px;
  background: #fafafa;
  border-top: 1px dashed rgba(15, 23, 42, 0.08);
`;

interface ProductGroup {
  productId: string;
  product: Product | undefined;
  totalQuantity: number;
  totalShippedQuantity: number;
  lines: OrderItem[];
}

interface SerialEntry {
  serial: string;
  parcelIndex: number;
  serialIdx: number;
}

const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
  orderItems = [],
  parcels = [],
  highlightedItemId = null,
  onBatchScan,
  onModalOpenChange,
  onSerialNumberClick,
}) => {
  const { t } = useTranslation();
  const lineRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [ scannedItemIds, setScannedItemIds ] = useState<Set<string>>(new Set());
  const [ batchScanItem, setBatchScanItem ] = useState<OrderItem | null>(null);

  // Group items by product
  const groupedProducts = useMemo<ProductGroup[]>(() => {
    const groups: ProductGroup[] = [];
    const groupMap: Record<string, ProductGroup> = {};

    orderItems.forEach(item => {
      const productId = item.product?.id || 'unknown';
      if (!groupMap[productId]) {
        groupMap[productId] = {
          productId,
          product: item.product,
          totalQuantity: 0,
          totalShippedQuantity: 0,
          lines: []
        };
        groups.push(groupMap[productId]);
      }

      const group = groupMap[productId];
      group.lines.push(item);
      group.totalQuantity += (item.quantity || 0);
      group.totalShippedQuantity += (item.shipped_quantity || 0);
    });

    return groups;
  }, [ orderItems ]);

  // 從 parcels 統整每個 item 的序號（包含 parcel index 資訊）
  // 配對規則跟 recalculateOrderItemsFromParcels 一致:
  //   - 優先用 parcel_item.fulfillment_item_id 精確配對(主路徑都會帶)
  //   - 缺欄位才退回 (product_id, batch) 廣播當 safety net
  // 沒這層精確配對的話,同商品+同 batch 多 FI 的情境(例如同商品但 inventory_type 不同)
  // 每個 row 都會顯示完整 serials,user 視覺上看到「兩個 row 序號一模一樣」。
  const getSerialNumbersFromParcels = (item: OrderItem): SerialEntry[] => {
    const serialNumbersWithParcelIndex: SerialEntry[] = [];

    parcels.forEach((parcel, pIdx) => {
      parcel.parcel_items?.forEach((parcelItem) => {
        const productAndBatchMatch =
          parcelItem.product_id === item.product?.id &&
          (parcelItem.batch === item.batch || (!parcelItem.batch && !item.batch));
        if (!productAndBatchMatch) return;

        const hasFulfillmentItemId = parcelItem.fulfillment_item_id != null;
        if (hasFulfillmentItemId && parcelItem.fulfillment_item_id !== item.id) {
          return;
        }

        if (parcelItem.serial_numbers && Array.isArray(parcelItem.serial_numbers)) {
          parcelItem.serial_numbers.forEach((serial, serialIdx) => {
            serialNumbersWithParcelIndex.push({
              serial,
              parcelIndex: pIdx,
              // serialIdx 用來幫 React key 去重 — 同一 parcel_item 內若出現重複 serial 值
              // (即使 #2 fix 後應該擋掉,還是要 defensive),只靠 serial+parcelIndex 會撞 key。
              serialIdx,
            });
          });
        }
      });
    });

    return serialNumbersWithParcelIndex;
  };

  const handleSerialNumberClick = (item: OrderItem, serialNumber: string, parcelIndex: number) => {
    if (onSerialNumberClick) {
      onSerialNumberClick(item, serialNumber, parcelIndex);
    }
  };

  // 當有新的 highlightedItemId 時，加入到 scannedItemIds
  useEffect(() => {
    if (highlightedItemId) {
      setScannedItemIds(prev => new Set(prev).add(highlightedItemId));
    }
  }, [ highlightedItemId ]);

  useEffect(() => {
    if (highlightedItemId && lineRefs.current[highlightedItemId]) {
      lineRefs.current[highlightedItemId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [ highlightedItemId ]);

  const handleBatchScanClick = (item: OrderItem) => {
    setBatchScanItem(item);
    if (onModalOpenChange) {
      onModalOpenChange(true);
    }
  };

  const handleBatchScanConfirm = (quantity: number, serialNumbers?: string[]) => {
    if (batchScanItem && onBatchScan) {
      onBatchScan(batchScanItem, quantity, serialNumbers);
    }
    setBatchScanItem(null);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };

  const handleBatchScanCancel = () => {
    setBatchScanItem(null);
    if (onModalOpenChange) {
      onModalOpenChange(false);
    }
  };

  return (
    <Container>
      <ItemsWrapper>
        {groupedProducts.map((group) => {
          const isGroupCompleted = group.totalShippedQuantity >= group.totalQuantity;

          return (
            <ProductCard key={group.productId}>
              <ProductHeader>
                <ProductMainInfo>
                  <ProductName>{group.product?.name || '--'}</ProductName>
                  <ProductMeta>
                    <SkuLabel>{group.product?.sku || '--'}</SkuLabel>
                    <span>{t('operator.qc.order_items_table.barcode')}: {group.product?.barcode || '--'}</span>
                  </ProductMeta>
                </ProductMainInfo>
                <TotalQuantityInfo>
                  <TotalQuantityLabel>{t('operator.qc.order_items_table.shipped_quantity')}</TotalQuantityLabel>
                  <TotalQuantityValue completed={isGroupCompleted}>
                    {group.totalShippedQuantity} / {group.totalQuantity}
                  </TotalQuantityValue>
                </TotalQuantityInfo>
              </ProductHeader>

              <LinesContainer>
                {group.lines.map((line) => {
                  const isLineCompleted = (line.shipped_quantity || 0) >= (line.quantity || 0);
                  const isHighlighted = line.id === highlightedItemId;
                  const isRecentlyScanned = scannedItemIds.has(line.id);
                  const lineNeedsSerial = (line.product?.required_serial_numbers_count ?? 0) > 0;
                  const serialNumbersWithIndex = lineNeedsSerial ? getSerialNumbersFromParcels(line) : [];

                  return (
                    <React.Fragment key={line.id}>
                      <LineItem
                        ref={(el) => { lineRefs.current[line.id] = el; }}
                        $highlighted={isHighlighted}
                        $recentlyScanned={isRecentlyScanned}
                      >
                        <LineDetails>
                          <DetailBox>
                            <DetailLabel>{t('operator.qc.order_items_table.inventory_type')}</DetailLabel>
                            <DetailValue>{line.inventory_type?.name || '--'}</DetailValue>
                          </DetailBox>
                          <DetailBox>
                            <DetailLabel>{t('operator.qc.order_items_table.batch')}</DetailLabel>
                            <DetailValue mono>{line.batch || '--'}</DetailValue>
                          </DetailBox>
                          <DetailBox>
                            <DetailLabel>{t('operator.qc.order_items_table.expiration_date')}</DetailLabel>
                            <DetailValue>{line.expiration_date || '--'}</DetailValue>
                          </DetailBox>
                          {line.note && (
                            <DetailBox>
                              <DetailLabel>{t('operator.qc.order_items_table.note')}</DetailLabel>
                              <Tooltip title={line.note}>
                                <DetailValue style={{ color: '#0369a1', fontWeight: 600 }}>{line.note}</DetailValue>
                              </Tooltip>
                            </DetailBox>
                          )}
                        </LineDetails>

                        <LineQuantity>
                          <QuantityBox completed={isLineCompleted}>
                            <QtyVal completed={isLineCompleted}>{line.shipped_quantity || 0}</QtyVal>
                            <QtyTotal>/ {line.quantity || 0}</QtyTotal>
                          </QuantityBox>

                          {!isLineCompleted && (
                            <Button
                              type="primary"
                              ghost
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => handleBatchScanClick(line)}
                              style={{ borderRadius: '6px' }}
                            />
                          )}
                        </LineQuantity>
                      </LineItem>

                      {lineNeedsSerial && serialNumbersWithIndex.length > 0 && (
                        <SerialSection>
                          <div style={{ marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8' }}>
                              {t('operator.qc.order_items_table.serial_numbers')} ({serialNumbersWithIndex.length})
                            </span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {serialNumbersWithIndex.map(({ serial, parcelIndex, serialIdx }) => (
                              <Tag
                                key={`${parcelIndex}-${serialIdx}-${serial}`}
                                style={{ fontSize: '11px', fontFamily: 'monospace', cursor: 'pointer' }}
                                onClick={() => handleSerialNumberClick(line, serial, parcelIndex)}
                              >
                                {serial}
                              </Tag>
                            ))}
                          </div>
                        </SerialSection>
                      )}
                    </React.Fragment>
                  );
                })}
              </LinesContainer>
            </ProductCard>
          );
        })}
      </ItemsWrapper>

      <BatchScanModal
        open={!!batchScanItem}
        item={batchScanItem}
        onConfirm={handleBatchScanConfirm}
        onCancel={handleBatchScanCancel}
      />
    </Container>
  );
};

export default OrderItemsTable;
